import { AxiosResponse } from "axios";
import Money from "common/values/money/money";
import _ from "lodash";
import moment from "moment";

export type ObjectDiffResults = {
  added: object,
  updated: object,
  removed: object,
  unchanged: object
};
/**
 * @description - Get the difference between two objects
 * @param {object} object1 - The first object to compare
 * @param {object} object2 - The second object to compare
 * @returns {ObjectDiffResults} - An object containing the differences between the two objects
 * @example - diffObjects({ a: 1, b: 2 }, { a: 1, b: 3 }) // { updated: { b: { newValue: 3, oldValue: 2 } } }
 * @example - diffObjects({ a: 1, b: 2 }, { a: 1, b: 2 }) // { unchanged: { a: 1, b: 2 } }
 * @example - diffObjects({ a: 1, b: 2 }, { a: 1 }) // { removed: { b: 2 } }
 */
export function diffObjects(object1: object = {}, object2: object = {}): ObjectDiffResults {
  let added: object = {};
  let updated: object = {};
  let removed: object = {};
  let unchanged: object = {};

  for (const prop in object1) {
    if (object1.hasOwnProperty(prop)) {
      if (object2.hasOwnProperty(prop)) {
        if (_.isEqual(object2[prop as keyof object], object1[prop as keyof object]))
          unchanged[prop as keyof object] = object1[prop as keyof object];
        else
          Object.defineProperty(updated, 'newValue', {
            value: object2[prop as keyof object]
          });
      } else {
        removed[prop as keyof object] = object1[prop as keyof object];
      }
    }
  }
  for (const prop in object2) {
    if (object2.hasOwnProperty(prop)) {
      if (object1.hasOwnProperty(prop)) {
        if (!_.isEqual(object1[prop as keyof object], object2[prop as keyof object]))
          Object.defineProperty(updated, 'oldValue', {
            value: object1[prop as keyof object]
          });
      } else {
        added[prop as keyof object] = object2[prop as keyof object];
      }
    }
  }

  return { added, updated, removed, unchanged } as ObjectDiffResults;
}

/**
 * @description - Formats a number to a currency string
 * @param {string} money - The money object to format
 * @returns {string} - The formatted currency string
 */
export function formatCurrency(money: Money|undefined): string {
  if (!money) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency.toString()
  }).format(money.amount);
}

/**
 * @description - Returns a native type from a string
 * @param {string} string - The string to parse
 * @returns {unknown} - The native type
 * @example - parseStringToNativeType('true') // true
 * @example - parseStringToNativeType('false') // false
 * @example - parseStringToNativeType('1.1') // 1.1
 */
export function parseStringToNativeType(string: string): unknown {
  if (!_.isString(string)) return string;

  if (Number.isNaN(Number.parseFloat(string))) {
    if (string?.toLowerCase() === 'true') return true;
    if (string?.toLowerCase() === 'false') return false;
    return string;
  }

  return Number.parseFloat(string);
}

/**
 * @description - Initiates a file download from the given response
 * @param {object} response - The response object from the axios request
 * @param {string} fileName - (optional) The name of the downloaded file (defaults to the file name from the response header)
 * @returns {Promise} - A promise that resolves when the file download is initiated
 */
export async function downloadFile(response: AxiosResponse, fileName?: string): Promise<void> {
  if (!response) throw new Error('No response provided');

  const fileType = response.headers['content-type'];
  const contentDisposition = response.request.getResponseHeader('Content-Disposition') as string;
  const matches = new RegExp(/(?<=filename(?:=|\*=(?:[\w-]+'')))["']?(?<filename>[^"';\n]+)["']?/).exec(contentDisposition);
  const blob = new Blob([response.data], { type: fileType });
  const link = document.createElement('a');
  link.setAttribute("href", globalThis.URL.createObjectURL(blob));
  if (fileName) {
    link.setAttribute("download", fileName);
  } else if (matches?.groups?.filename) {
    link.setAttribute("download", matches.groups?.filename);
  }
  link.setAttribute("target", "_blank");
  link.click();
}

/**
 * @description - Gets the part of the day based on the current time
 * @returns {string} - A greeting based on the current time of day
 * @example - getPartOfDay() // "Good morning" or "Good afternoon" or "Good evening"
 */
export function getPartOfDay(): string {
  const currentTime = moment();
  const splitAfternoon = 12;  // 24hr time to split the afternoon
  const splitEvening = 17;    // 24hr time to split the evening
  const currentHour = Number.parseFloat(currentTime.format('HH'));

  if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
    // Between 12 PM and 5PM
    return 'Good afternoon';
  } else if (currentHour >= splitEvening) {
    // Between 5PM and Midnight
    return 'Good evening';
  }
  // Between dawn and noon
  return 'Good morning';
}

/**
 *@description Adds spaces between title-case words 
 * (e.g., "ProposalResourceAccess" → "Proposal Resource Access").
 * Handles acronyms like "APIKey" → "API Key".
 * @param input - The title-case string to add spaces to.
 * @returns The input string with spaces added between title-case words.
 */
export function addSpacesToTitleCase(input: string): string {
  return input
    .replaceAll(/([a-z])([A-Z])/g, "$1 $2")        // camelCase → space before capital
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")  // Handle acronyms: APIKey → API Key
    .trim();
}

/**
 * @description - Gets the keys of an enum
 * @param {object} enumObject - The enum object
 * @returns {number[]} - An array of the enum keys
 * @example - getEnumKeys(MyEnum) // [0, 1, 2]
 */
export function enumKeys(enumObject: object): string[] {
  return Object.keys(enumObject).filter((k) => Number.isNaN(Number(k)));
}

/**
 * @description - Gets the key of an enum by its value
 * @param {object} enumObject - The enum object
 * @param {unknown} value - The value to find the key for
 * @returns {string} - The key of the enum or an empty string if not found
 */
export function enumKeyOf<T extends EnumLike>(enumObject: T, value: unknown): string {
  if (value == null) return '';

  // Numeric enums (reverse mapping exists: 1 -> "Foo")
  if (typeof value === 'number') {
    const maybe = (enumObject as { [k: number]: string | number })[value];
    return typeof maybe === 'string' ? maybe : '';
  }

  // String enums (no reverse map): the value is a string
  if (typeof value === 'string') {
    // If it's already a key, return it
    if (Object.hasOwn(enumObject, value)) return value;

    // Otherwise, try to find the key whose value matches this string
    for (const [k, v] of Object.entries(enumObject)) {
      if (v === value) return k;
    }

    // Last resort: treat as-is
    return value;
  }

  // Anything else (objects, booleans, symbols, etc.)
  return '';
}

/**
 * @description - Parses a string key into its corresponding enum value
 * @param {object} enumObject - The enum object
 * @param {string} key - The key to parse
 * @returns {unknown} - The enum value or undefined if not found
 */
export function enumParse<T extends EnumLike>(enumObject: T, key: string): unknown {
  return enumObject[key as keyof T];
}
